import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';

import { Input, BaseButton, Modal, ToolTip, useT } from '@repo/common';
import LinkList from './LinkList';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as LinkIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';

const StyledLinkIcon = ({ $state }) => (
  <LinkIcon
    className={classNames({
      'text-icon-primary': $state === 'active',
      'text-icon-placeholder': $state === 'default',
    })}
  />
);

const getIconState = (value, fieldState) => {
  if (value) return 'active';
  return 'default';
};

const LinkForm = ({ link, setResumeData }) => {
  const t = useT();
  const [links, setLinks] = useState([
    {
      id: uuidv4(),
      linkType: '',
      link: '',
    },
  ]);

  const [openLinkTypeId, setOpenLinkTypeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);
  const inputRefs = useRef({});
  const linkRefs = useRef({});
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (isInitialized) return;

    if (!link || link.length === 0) {
      setLinks([
        {
          id: uuidv4(),
          linkType: '',
          link: '',
        },
      ]);
    } else {
      setLinks(
        link.map((l) => ({
          id: l.id || uuidv4(),
          linkType: l.linkType || '',
          link: l.link || '',
        })),
      );
    }

    setIsInitialized(true);
  }, [link, isInitialized]);

  const addLink = () => {
    const newLlink = {
      id: uuidv4(),
      linkType: '',
      link: '',
    };
    setLinks((prev) => [...prev, newLlink]);
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const updatedLinks = links.filter((l) => l.id !== targetId);
    setLinks(updatedLinks);
    setResumeData((prev) => ({ ...prev, link: updatedLinks }));
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleInputChange = (id, field, value) => {
    const newlinks = links.map((link) =>
      link.id === id ? { ...link, [field]: value } : link,
    );
    setLinks(newlinks);

    setResumeData((prev) => ({
      ...prev,
      link: newlinks,
    }));
  };

  const toggleLinkTypeList = (id, e) => {
    e.stopPropagation();
    setOpenLinkTypeId((prev) => (prev === id ? null : id));
  };

  const handleSelectLinkType = (id, link) => {
    handleInputChange(id, 'linkType', link);
    setOpenLinkTypeId(null);
    inputRefs.current[id]?.blur();
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(links);
    const [reordered] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reordered);
    setLinks(items);
    setResumeData((prev) => ({ ...prev, link: items }));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openLinkTypeId) {
        const ref = linkRefs.current[openLinkTypeId];
        if (ref && !ref.contains(event.target)) {
          setOpenLinkTypeId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openLinkTypeId]);

  return (
    <div className='flex flex-col items-start w-full min-w-[460px] max-w-[950px] gap-6'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='links' direction='vertical'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-6 w-full'
            >
              {links.map((link, index) => (
                <Draggable key={link.id} draggableId={link.id} index={index}>
                  {(provided, snapshot) => {
                    const iconState = getIconState(link.link);
                    const style = {
                      ...provided.draggableProps.style,
                      transform: provided.draggableProps.style?.transform
                        ? provided.draggableProps.style.transform.replace(
                            /translate\(([-\d.]+)px,/,
                            'translate(0px,',
                          )
                        : undefined,
                    };

                    if (!snapshot.isDragging) {
                      delete style.transition;
                    }

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex w-full items-start`}
                      >
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray03 hover:text-icon-success active:text-icon-success'
                          onMouseEnter={() => setHoverDragAndDropId(link.id)}
                          onMouseLeave={() => setHoverDragAndDropId(null)}
                        >
                          <DragAndDropIcon width={33} height={33} />
                          {hoverDragAndDropId === link.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Click and drag to move', '클릭한 채 드래그하여 이동')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>

                        {/* Links Form */}
                        <div className='flex-grow bg-grayscale-gray00'>
                          <div className='flex flex-row flex-wrap items-start content-start p-0 gap-4'>
                            {/* Link type */}
                            <div className='flex flex-col gap-2 min-w-[160px] max-w-[200px]'>
                              <div
                                ref={(el) => (linkRefs.current[link.id] = el)}
                                onClick={(e) => toggleLinkTypeList(link.id, e)}
                                className='relative'
                              >
                                <Input
                                  ref={(el) =>
                                    (inputRefs.current[link.id] = el)
                                  }
                                  label=''
                                  placeholder={t('Link type', '링크 유형')}
                                  value={link.linkType}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openLinkTypeId === link.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openLinkTypeId === link.id && (
                                  <div className='absolute top-[52px] left-0 z-20 w-full'>
                                    <LinkList
                                      onSelect={(l) =>
                                        handleSelectLinkType(link.id, l)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Link URL */}
                            <div className='flex flex-col gap-2 min-w-[360px] flex-grow'>
                              <Input
                                ref={(el) =>
                                  (inputRefs.current[`url-${link.id}`] = el)
                                }
                                label=''
                                placeholder={t('Please enter the link', '링크를 입력해 주세요')}
                                value={link.link}
                                onChange={(e) =>
                                  handleInputChange(
                                    link.id,
                                    'link',
                                    e.target.value,
                                  )
                                }
                                leftIcon={<StyledLinkIcon $state={iconState} />}
                                hideCount
                              />
                            </div>
                          </div>
                        </div>

                        {/* Delete */}
                        <div className='flex-shrink-0 pt-1 relative'>
                          <button
                            className='w-10 h-10 flex justify-center items-center text-grayscale-gray04 hover:text-icon-success transition-colors'
                            onClick={() => openConfirmModal(link.id)}
                            onMouseEnter={() => setHoverDeleteId(link.id)}
                            onMouseLeave={() => setHoverDeleteId(null)}
                          >
                            <DeleteIcon width={24} height={24} />
                          </button>
                          {hoverDeleteId === link.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Delete', '삭제')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className='w-full pl-[25px] pr-[40px]'>
        <BaseButton
          type='secondary'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addLink}
        />
      </div>

      {showDeleteModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this link entry?', '이 링크 항목을 삭제할까요?')}
          subText={t('This action can’t be undone.', '삭제 후에는 되돌릴 수 없습니다.')}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default LinkForm;
